import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api.ts';
import { MFA_EMAIL_STORAGE_KEY, MFA_TOKEN_STORAGE_KEY, verifyMfaLogin } from '../services/mfa.ts';

interface User {
  id: number | string;
  name: string;
  email: string;
  role: string;
  department?: string;
  position?: string;
  status?: 'active' | 'inactive';
  avatar?: string;
  mfaEnabled?: boolean;
}

export type LoginResult =
  | { status: 'authenticated' }
  | { status: 'mfa_required'; mfaToken: string; email: string };

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  completeMfaLogin: (mfaToken: string, code: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
  hasPermission: (permission: string) => boolean;
  hasResourceAccess: (resource: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Permission definitions
const permissions = {
  admin: [
    'users.create',
    'users.read',
    'users.update',
    'users.delete',
    'assets.create',
    'assets.read',
    'assets.update',
    'assets.delete',
    'debts.create',
    'debts.read',
    'debts.update',
    'debts.delete',
    'receivables.create',
    'receivables.read',
    'receivables.update',
    'receivables.delete',
    'reports.read',
    'settings.update',
    'settings.read',
    'whitelabel.update',
    // Empresas (companies)
    'companies.read',
    'companies.create',
    'companies.update',
    'companies.delete',
    // Investimentos (investments)
    'investments.read',
    'investments.create',
    'investments.update',
    'investments.delete',
  ],
  user: [
    'assets.create',
    'assets.read',
    'assets.update',
    'debts.create',
    'debts.read',
    'debts.update',
    'receivables.create',
    'receivables.read',
    'receivables.update',
    'reports.read',
    'profile.update',
    // Empresas: leitura
    'companies.read',
    // Investimentos: leitura e alteração
    'investments.read',
    'investments.create',
    'investments.update',
  ],
  viewer: [
    'assets.read',
    'debts.read',
    'receivables.read',
    'reports.read',
    'profile.update',
    'investments.read',
  ],
};

// Resource access definitions
const resourceAccess = {
  admin: [
    'dashboard',
    'assets',
    'users',
    'reports',
    'profile',
    'settings',
    'whitelabel',
    'debts',
    'receivables',
    'companies',
    'investments',
  ],
  user: [
    'dashboard',
    'assets',
    'reports',
    'profile',
    'debts',
    'receivables',
    'companies',
    'investments',
  ],
  viewer: ['dashboard', 'assets', 'reports', 'profile', 'receivables', 'investments'],
};

// Map backend roles to frontend permission groups
const mapRoleToGroup = (role: string): keyof typeof permissions => {
  const normalized = role?.toLowerCase();
  switch (normalized) {
    case 'admin':
      return 'admin';
    case 'user':
      return 'user';
    // Mapeamento para compatibilidade com roles antigos
    case 'director':
      return 'admin';
    case 'finance':
    case 'operations':
    case 'it':
      return 'user';
    default:
      return 'user';
  }
};

interface AuthProviderProps {
  children: ReactNode;
}

const persistSession = (
  token: string,
  backendUser: User,
  setUser: (u: User) => void,
  setIsAuthenticated: (v: boolean) => void
) => {
  localStorage.setItem('@AssetManagement:token', token);
  localStorage.setItem('@AssetManagement:user', JSON.stringify(backendUser));
  sessionStorage.removeItem(MFA_TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(MFA_EMAIL_STORAGE_KEY);
  (api.defaults.headers as any).common = (api.defaults.headers as any).common || {};
  (api.defaults.headers as any).common['Authorization'] = `Bearer ${token}`;
  setUser(backendUser);
  setIsAuthenticated(true);
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('@AssetManagement:token');
    const storedUser = localStorage.getItem('@AssetManagement:user');

    if (storedToken && storedUser) {
      setUser(JSON.parse(storedUser));
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<LoginResult> => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const payload = response.data;

      // Some backends wrap data; handle both cases
      const data = payload?.data || payload;

      if (data?.mfaRequired && data?.mfaToken) {
        sessionStorage.setItem(MFA_TOKEN_STORAGE_KEY, data.mfaToken);
        sessionStorage.setItem(MFA_EMAIL_STORAGE_KEY, data.email || email);
        return {
          status: 'mfa_required',
          mfaToken: data.mfaToken,
          email: data.email || email,
        };
      }

      const token: string = data.token;
      const backendUser = data.user;

      if (!token || !backendUser) {
        throw new Error('Resposta de login inválida');
      }

      persistSession(token, backendUser as User, setUser, setIsAuthenticated);
      return { status: 'authenticated' };
    } catch (error: any) {
      console.error('Login error:', error);

      // Verificar se é um erro de network
      if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error' || !error.response) {
        throw new Error(
          'Erro de conexão. Verifique se o servidor está funcionando e tente novamente.'
        );
      }

      // Verificar se é erro 401 (credenciais inválidas)
      if (error.response?.status === 401) {
        throw new Error('Email ou senha incorretos. Verifique suas credenciais.');
      }

      // Verificar se é erro 400 (dados inválidos)
      if (error.response?.status === 400) {
        const message = error.response?.data?.error || error.response?.data?.message;
        throw new Error(message || 'Dados de login inválidos. Verifique os campos preenchidos.');
      }

      // Verificar se é erro 500 (erro interno do servidor)
      if (error.response?.status === 500) {
        throw new Error('Erro interno do servidor. Tente novamente em alguns instantes.');
      }

      // Outros erros
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Erro ao autenticar';
      throw new Error(message);
    }
  };

  const completeMfaLogin = async (mfaToken: string, code: string): Promise<void> => {
    try {
      const { token, user: backendUser } = await verifyMfaLogin(mfaToken, code);
      persistSession(token, backendUser as User, setUser, setIsAuthenticated);
    } catch (error: any) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        'Código inválido. O código informado está incorreto ou expirou. Confira o autenticador e tente novamente.';
      throw new Error(message);
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('@AssetManagement:token');
    localStorage.removeItem('@AssetManagement:user');
    sessionStorage.removeItem(MFA_TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(MFA_EMAIL_STORAGE_KEY);
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    const group = mapRoleToGroup(String(user.role));
    return permissions[group]?.includes(permission) || false;
  };

  const canAccess = (resource: string): boolean => {
    if (!user) return false;
    const group = mapRoleToGroup(String(user.role));
    return resourceAccess[group]?.includes(resource) || false;
  };

  const value: AuthContextType = {
    user,
    login,
    completeMfaLogin,
    logout,
    isAuthenticated,
    loading,
    hasPermission,
    hasResourceAccess: canAccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
